import React, {
	Context,
	useState,
	useEffect,
	useRef,
	useCallback,
	useContext,
} from 'react';

// a hook to provide useAudioVideo with the value of SessionReducer.webrtcID
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../stores/types/types';

export interface AVData {
	id: string; // non 'local' is webrtcid of stream
	audio: boolean;
	video: boolean;
}

// partial updates let components write A or V without knowing the other,
// while avoiding undefined checks on the public interface
export interface PartialAV {
	id: string,
	audio?: boolean | undefined;
	video?: boolean | undefined;
}

export type AVDataSet = Record<string, AVData>
export interface AVHookInterface {
	setAV: (newData: PartialAV) => void;
	getAV: (id?: string) => AVData;
	getAllAV: () => AVDataSet;
	removeAV: (id?: string) => void;
}

const baseAV = { id: 'local', audio: true, video: true };
const baseAVSet = { local: baseAV };

// what you call to gain access to the useAVContext hook from components
export const AVContext = React.createContext({
	setAV: () => undefined,
	getAV: () => ({}),
	getAllAV: () => ({}),
	removeAV: () => undefined,
}) as unknown as Context<AVHookInterface>;

// what you pass into the React.provider component as the value
export function useAudioVideo(): AVHookInterface {
	// the redux state for our main publisher's webrtcid
	// used to see if remote updates match, without hooking into it.
	// would otherwise check match in components
	const mainPubWebrtcId = useSelector((s: GS) => s.SessionReducer.webrtcID);
	const webrtcIDRef: React.MutableRefObject<GS['SessionReducer']['webrtcID']> = useRef(mainPubWebrtcId);
	useEffect(() => { webrtcIDRef.current = mainPubWebrtcId; }, [mainPubWebrtcId]);

	// the AV set, keyed by webrtcid
	const [avData, setAVData] = useState<AVDataSet>(baseAVSet);

	// the ref to the AV set, exported
	const avDataRef = useRef<AVDataSet>({} as AVDataSet);
	avDataRef.current = avData;

	// localize foreign ids when they match the id of our redux (OT publisher) webrtcid
	const localizeRtcId = (id: string) => ((id === webrtcIDRef.current) ? 'local' : id);

	const isFirstLoad = useRef(true);
	const getAVFromLocalStorage = (): AVDataSet => {
		// preffered state is passed into opentok class' startStream()
		const avDataLocal = localStorage.getItem('avData');
		return avDataLocal ? JSON.parse(avDataLocal) : {};
	};

	const setAVToLocalStorage = (newData: AVData): void => {
		// AVstate is mirrored in localstorage, for refreshes and in getAV()
		const storage = getAVFromLocalStorage();
		storage[newData.id] = newData;
		localStorage.setItem('avData', JSON.stringify(storage));
	};

	const setAV = useCallback((incomingData: PartialAV): void => {
		const newData = {
			...incomingData,
			id: localizeRtcId(incomingData.id),
		};

		if (isFirstLoad.current && newData.id === 'local') {
			isFirstLoad.current = false;
			const avLocalStorage = getAVFromLocalStorage();
			Object.assign(newData, avLocalStorage.local);
		}

		setAVData((prev: AVDataSet) => {
			const newAV = { ...prev[newData.id], ...newData };
			if (newData.id === 'local') {
				setAVToLocalStorage(newAV);
			}
			return { ...prev, [newData.id]: { ...newAV } };
		});
	}, []);

	// most of the GUI elements only care about the local audio and video settings
	const getAV = useCallback((webrtcId: string = 'local'): AVData => {
		const localizedId = localizeRtcId(webrtcId);

		const avState = { ...baseAV, ...avDataRef.current[localizedId] };

		// all AV states could live in localstorage if we want to persist all
		if (isFirstLoad.current && localizedId === 'local') {
			const stored = getAVFromLocalStorage()?.local;
			Object.assign(avState, stored || {});
		}

		return { ...baseAV, ...avState, id: localizedId };
	}, []);

	const getAllAV = useCallback((): AVDataSet => ({ ...avDataRef.current }), []);

	// most functionality is local client, so it is default
	const removeAV = useCallback((webrtcid: string = 'local'): void => {
		setAVData((prev: AVDataSet) => {
			const newSet = prev;
			delete newSet[webrtcid];
			return newSet;
		});
	}, []);

	useEffect(() => setAV(baseAV), []);

	return {
		setAV,
		getAV,
		getAllAV,
		removeAV,
	};
}

export const AVProvider = (
	props: any,
): React.ReactElement<any, React.Provider<AVHookInterface>> => {
	const avContextValue = useAudioVideo();
	return <AVContext.Provider value={avContextValue}>
		{props.children}
	</AVContext.Provider>;
};

export const useAVHasUnmutedAudio = (): boolean => {
	const { getAllAV } = useContext(AVContext);
	const [anyStreamUnmuted, setAnyStreamUnmuted] = useState(false);
	const hasAudioRef = useRef(false);

	hasAudioRef.current = Object.values(getAllAV()).some((av) => av.audio);

	if (hasAudioRef.current !== anyStreamUnmuted) {
		setAnyStreamUnmuted(hasAudioRef.current);
	}

	return anyStreamUnmuted;
};
